import { useState, useRef, useEffect, lazy, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable/index";
import { toast } from "sonner";
import { isDisposableEmail, checkSignupRateLimit, recordSignupAttempt, isValidEmailFormat } from "@/lib/signup-protection";
import { useTranslation } from "react-i18next";
import { motion, useInView, AnimatePresence } from "framer-motion";
import ReactMarkdown from "react-markdown";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Loader2,
  ArrowRight,
  ChevronDown,
  Sun,
  Moon,
  Zap,
  Shield,
  Globe,
  BarChart3,
  Rocket,
  Target,
  Bot,
  Layers,
  TrendingUp,
  Package,
  CreditCard,
  Check,
  Heart,
  Quote,
  Users,
  Mail,
  Phone,
  MapPin,
  MessageCircle,
  Clock,
  Award,
  Handshake,
  Search,
  Sparkles,
  Play,
  CircleDot,
} from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import { LanguageSelector } from "@/components/LanguageSelector";

const MARQUEE_BRANDS: { name: string; logo?: string; color: string }[] = [
  { name: "Shopify", logo: "/brands/shopify.svg", color: "#96BF48" },
  { name: "Salla", logo: "/brands/salla.svg", color: "#8B5CF6" },
  { name: "Youcan", logo: "/brands/youcan.jpg", color: "#C2185B" },
  
  { name: "WooCommerce", logo: "/brands/woocommerce.svg", color: "#96588A" },
  { name: "Meta Ads", logo: "/brands/meta.svg", color: "#0081FB" },
  { name: "Google Ads", logo: "/brands/google.svg", color: "#4285F4" },
  { name: "TikTok Shop", logo: "/brands/tiktok.svg", color: "#00F2EA" },
  { name: "Snapchat Ads", logo: "/brands/snapchat.webp", color: "#FFFC00" },
  { name: "Aramex", logo: "/brands/aramex.png", color: "#E2231A" },
  { name: "DHL", logo: "/brands/dhl.svg", color: "#FFCC00" },
  { name: "J&T Express", logo: "/brands/jtexpress.png", color: "#E30613" },
  
  { name: "Alibaba", logo: "/brands/alibaba.svg", color: "#FF6A00" },
  { name: "AliExpress", logo: "/brands/aliexpress.svg", color: "#E43225" },
  { name: "Amazon", logo: "/brands/amazon.svg", color: "#FF9900" },
  { name: "Noon", logo: "/brands/noon.png", color: "#FEEE00" },
  { name: "Stripe", logo: "/brands/stripe.svg", color: "#635BFF" },
  { name: "PayPal", logo: "/brands/paypal.svg", color: "#003087" },
  
  { name: "Fawry", logo: "/brands/fawry.png", color: "#FFB800" },
  { name: "COD Network", logo: "/brands/codnetwork.png", color: "#F5A623" },
  { name: "Done", logo: "/brands/done.webp", color: "#F04D23" },
];

interface Testimonial {
  name: string;
  role: string;
  country: string;
  flag: string;
  text: string;
  rating: number;
  avatar: string;
  gender: "male" | "female";
}

const TESTIMONIALS_ROW1: Testimonial[] = [
  { name: "Youssef El Amrani", role: "Fondateur & PDG", country: "Maroc", flag: "🇲🇦", text: "Le copilote IA de Vazoul nous a aidés à réduire nos dépenses publicitaires de 35% tout en triplant les conversions. C'est comme avoir une équipe de croissance complète.", rating: 5, avatar: "YA", gender: "male" },
  { name: "لينا حداد", role: "مديرة التجارة الإلكترونية", country: "الإمارات", flag: "🇦🇪", text: "ربطنا فازول مع متجرنا على شوبيفاي في دقائق. تنبيهات المخزون الذكية وحدها أنقذتنا من 3 نفاذات مخزون الشهر الماضي.", rating: 4, avatar: "LH", gender: "female" },
  { name: "Carlos Méndez", role: "Growth Marketer", country: "España", flag: "🇪🇸", text: "El estudio creativo es revolucionario. Genero guiones UGC, fotos de productos y landing pages — todo desde un solo panel.", rating: 5, avatar: "CM", gender: "male" },
  { name: "Amira Benali", role: "Propriétaire de marque", country: "Algérie", flag: "🇩🇿", text: "Gérer les commandes COD était un cauchemar. L'intelligence de confirmation de Vazoul a boosté mon taux de livraison de 58% à 87% !", rating: 4, avatar: "AB", gender: "female" },
  { name: "David Okonkwo", role: "Dropshipping Pro", country: "Nigeria", flag: "🇳🇬", text: "Product discovery AI found me 5 winning products in 10 days. The profit simulator told me exactly which ones to scale.", rating: 5, avatar: "DO", gender: "male" },
  { name: "Emma Johansson", role: "Digital Entrepreneur", country: "Sweden", flag: "🇸🇪", text: "I've tried dozens of tools. Vazoul is the first that actually connects ads, orders, and profits into one clear picture.", rating: 3, avatar: "EJ", gender: "female" },
  { name: "خالد الرشيدي", role: "رائد أعمال", country: "السعودية", flag: "🇸🇦", text: "فازول مو بس أداة — هو شريك نجاح. التقارير الذكية والتوصيات اليومية غيّرت طريقة اتخاذ قراراتي بالكامل.", rating: 5, avatar: "KR", gender: "male" },
];

const TESTIMONIALS_ROW2: Testimonial[] = [
  { name: "فاطمة السيد", role: "مديرة تسويق", country: "مصر", flag: "🇪🇬", text: "التقارير الذكية بتوفّر عليّا ٤ ساعات كل أسبوع. بحصل على رؤى كانت محتاجة فريق تحليلات كامل.", rating: 4, avatar: "FS", gender: "female" },
  { name: "Mehdi Touzani", role: "Propriétaire de boutique", country: "Tunisie", flag: "🇹🇳", text: "La passerelle d'expédition de Vazoul m'a connecté aux meilleurs transporteurs automatiquement. Les frais de livraison ont baissé de 40% du jour au lendemain.", rating: 5, avatar: "MT", gender: "male" },
  { name: "Sophie Moreau", role: "Directrice Marketing", country: "France", flag: "🇫🇷", text: "L'assistant IA est disponible 24/7 et ses recommandations sont toujours pertinentes. Nos conversions ont augmenté de 220%.", rating: 3, avatar: "SM", gender: "female" },
  { name: "Omar Khalifi", role: "Vendeur COD", country: "Maroc", flag: "🇲🇦", text: "Avant Vazoul, je perdais 40% des commandes à cause de livraisons échouées. Maintenant mon taux de confirmation est à 92%. Un vrai sauveur.", rating: 5, avatar: "OK", gender: "male" },
  { name: "Priya Sharma", role: "E-commerce Consultant", country: "India", flag: "🇮🇳", text: "I recommend Vazoul to all my clients. The all-in-one approach — ads, inventory, shipping, AI — is unmatched in the market.", rating: 4, avatar: "PS", gender: "female" },
  { name: "عبدالرحمن العتيبي", role: "صاحب متجر", country: "السعودية", flag: "🇸🇦", text: "الستوديو الإبداعي وفّر عليّ آلاف الريالات في التصميم. أسوي إعلانات احترافية بضغطة زر!", rating: 5, avatar: "AA", gender: "male" },
  { name: "Isabella Costa", role: "Empreendedora", country: "Brasil", flag: "🇧🇷", text: "Vazoul me ajudou a lançar em 3 novos mercados sem contratar ninguém. A automação cuida de tudo, dos anúncios ao fulfillment.", rating: 3, avatar: "IC", gender: "female" },
  { name: "Tom Nguyen", role: "Performance Marketer", country: "Australia", flag: "🇦🇺", text: "The ad attribution funnel is brilliant. I finally know exactly which campaigns drive real profit, not just clicks.", rating: 4, avatar: "TN", gender: "male" },
];

function TestimonialCard({ review, index }: { review: Testimonial; index: number }) {
  const genderGradient = review.gender === "female"
    ? "from-[hsl(330,70%,65%)] to-[hsl(280,65%,58%)]"
    : "from-[hsl(220,75%,55%)] to-[hsl(262,70%,55%)]";

  const accentBorder = review.gender === "female"
    ? "hover:border-[hsl(330,70%,65%,0.4)]"
    : "hover:border-[hsl(262,70%,60%,0.4)]";

  const glowColor = review.gender === "female"
    ? "hsl(330,70%,65%,0.08)"
    : "hsl(262,70%,55%,0.08)";

  return (
    <div
      className={`shrink-0 w-[280px] sm:w-[320px] group relative rounded-2xl border border-border/50 bg-card/60 backdrop-blur-md p-5 space-y-3.5 ${accentBorder} transition-all duration-500`}
      style={{ boxShadow: `0 0 0 0 transparent` }}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = `0 12px 40px -12px ${glowColor}, 0 0 0 1px ${glowColor}`; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = `0 0 0 0 transparent`; }}
    >
      {/* Subtle corner accent */}
      <div className={`absolute top-0 left-0 w-16 h-16 rounded-tl-2xl bg-gradient-to-br ${genderGradient} opacity-[0.04] group-hover:opacity-[0.08] transition-opacity duration-500`} />

      {/* Quote icon */}
      <div className="absolute top-4 right-4 opacity-[0.05] group-hover:opacity-[0.1] transition-opacity duration-300">
        <Quote className="h-8 w-8 text-foreground" />
      </div>

      {/* Author - moved to top */}
      <div className="flex items-center gap-3">
        <div
          className={`h-10 w-10 rounded-xl bg-gradient-to-br ${genderGradient} flex items-center justify-center text-white text-sm font-bold shadow-md ring-2 ring-border/30`}
        >
          {review.avatar}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-[13px] font-bold text-foreground truncate">{review.name}</span>
            <span className="text-sm leading-none">{review.flag}</span>
          </div>
          <p className="text-[11px] text-muted-foreground truncate">{review.role} · {review.country}</p>
        </div>
      </div>

      {/* Review text */}
      <p className="text-[13px] text-foreground/75 leading-relaxed line-clamp-4 min-h-[65px]">"{review.text}"</p>

      {/* Stars - at bottom */}
      <div className="flex items-center gap-2">
        <div className="flex gap-0.5">
          {Array.from({ length: 5 }).map((_, i) => (
            <span key={i} className={`text-xs ${i < review.rating ? "text-amber-400 drop-shadow-[0_0_4px_rgba(251,191,36,0.5)]" : "text-muted-foreground/20"}`}>★</span>
          ))}
        </div>
        <span className="text-[10px] text-muted-foreground/50 font-medium">{review.rating}.0</span>
      </div>
    </div>
  );
}

// Scroll-triggered section wrapper — lightweight on mobile
function RevealSection({ children, className = "", delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-40px" });
  return (
    <div
      ref={ref}
      className={`${className} transition-opacity duration-500 ${isInView ? 'opacity-100' : 'opacity-0'}`}
    >
      {children}
    </div>
  );
}

// Animated counter
function AnimatedCounter({ value, suffix = "" }: { value: string; suffix?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const numericPart = parseInt(value.replace(/\D/g, "")) || 0;
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!isInView) return;
    let frame: number;
    const duration = 1200;
    const start = performance.now();
    const animate = (now: number) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.round(numericPart * eased));
      if (progress < 1) frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [isInView, numericPart]);

  return (
    <span ref={ref} className="text-2xl sm:text-3xl font-black gradient-text">
      {count}{suffix}
    </span>
  );
}

function PolicyModalContent({ type }: { type: "terms" | "privacy" }) {
  const [content, setContent] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const { i18n } = useTranslation();
  const lang = i18n.language?.slice(0, 2) || "en";

  useEffect(() => {
    setLoading(true);
    supabase
      .from("legal_policies")
      .select("title, content, version, title_ar, title_fr, title_es, content_ar, content_fr, content_es")
      .eq("policy_type", type)
      .eq("is_active", true)
      .order("version", { ascending: false })
      .limit(1)
      .maybeSingle()
      .then(({ data }) => {
        setContent(data);
        setLoading(false);
      });
  }, [type]);

  if (loading) return <div className="flex-1 flex items-center justify-center py-12"><div className="h-6 w-6 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>;
  if (!content) return <div className="flex-1 flex items-center justify-center py-12 text-muted-foreground text-sm">Policy not found.</div>;

  const localContent = (lang !== "en" && content[`content_${lang}`]) || content.content;

  return (
    <div className="flex-1 overflow-y-auto px-6 py-4" dir={lang === "ar" ? "rtl" : "ltr"}>
      <div className="prose prose-sm dark:prose-invert max-w-none text-foreground/90 [&_h1]:text-foreground [&_h2]:text-foreground [&_h3]:text-foreground [&_p]:text-foreground/80 [&_li]:text-foreground/80">
        <ReactMarkdown>{localContent}</ReactMarkdown>
      </div>
    </div>
  );
}

export default function Auth() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [loading, setLoading] = useState(false);
  const [policyAccepted, setPolicyAccepted] = useState(false);
  const [policyRead, setPolicyRead] = useState(false);
  const [showPolicyModal, setShowPolicyModal] = useState<"terms" | "privacy" | null>(null);
  const [forgotPassword, setForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const { theme, toggleTheme } = useTheme();

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      toast.success("Password reset email sent! Check your inbox.");
      setForgotPassword(false);
      setForgotEmail("");
    } catch (err: any) {
      toast.error(err.message || "Failed to send reset email");
    } finally {
      setForgotLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLogin && !policyAccepted) {
      toast.error(t("landing.policyRequired"));
      return;
    }

    // Signup protection checks
    if (!isLogin) {
      if (!isValidEmailFormat(email)) {
        toast.error("Please enter a valid email address.");
        return;
      }
      if (isDisposableEmail(email)) {
        toast.error("Temporary email addresses are not allowed. Please use a permanent email.");
        return;
      }
      const rateCheck = checkSignupRateLimit();
      if (!rateCheck.allowed) {
        const minutes = Math.ceil(rateCheck.retryAfterSeconds / 60);
        toast.error(`Too many signup attempts. Please try again in ${minutes} minute${minutes > 1 ? "s" : ""}.`);
        return;
      }
    }

    setLoading(true);
    try {
      if (isLogin) {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        toast.success("Logged in successfully");
      } else {
        // Record signup attempt for rate limiting
        recordSignupAttempt();

        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: { display_name: displayName },
            emailRedirectTo: window.location.origin,
          },
        });
        if (error) throw error;

        // Record terms acceptance on profile
        if (data.user?.id) {
          // Get active terms version
          const { data: activeTerms } = await supabase
            .from("legal_policies")
            .select("version")
            .eq("policy_type", "terms")
            .eq("is_active", true)
            .order("version", { ascending: false })
            .limit(1)
            .maybeSingle();

          await supabase.from("profiles").update({
            terms_accepted: true,
            privacy_accepted: true,
            terms_acceptance_timestamp: new Date().toISOString(),
            accepted_terms_version: activeTerms?.version || 1,
          }).eq("user_id", data.user.id);

          // Also record in policy_acceptances for traceability
          const { data: activePolicies } = await supabase
            .from("legal_policies")
            .select("id, policy_type, version")
            .eq("is_active", true);

          if (activePolicies?.length) {
            await supabase.from("policy_acceptances").insert(
              activePolicies.map((p) => ({
                user_id: data.user!.id,
                policy_id: p.id,
                policy_type: p.policy_type,
                policy_version: p.version,
                user_agent: navigator.userAgent,
              }))
            );
          }
        }

        if (data.session) {
          // User has a session (auto-confirmed). Check if pending approval.
          toast.success("Account created! Please verify your email to proceed.");
          await supabase.auth.signOut();
          return;
        } else {
          toast.success("Account created! Please check your email to verify your address. After verification, your account will be reviewed for approval.");
        }
      }
    } catch (err: any) {
      toast.error(err.message || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  const scrollToFeatures = () => {
    document.getElementById("features")?.scrollIntoView({ behavior: "smooth" });
  };

  const pillars = [
    { icon: Search, label: t("landing.pillar1"), color: "from-[hsl(262,80%,60%)] to-[hsl(280,75%,55%)]" },
    { icon: Rocket, label: t("landing.pillar2"), color: "from-[hsl(330,85%,60%)] to-[hsl(350,80%,55%)]" },
    { icon: Package, label: t("landing.pillar3"), color: "from-[hsl(145,70%,45%)] to-[hsl(160,65%,40%)]" },
    { icon: TrendingUp, label: t("landing.pillar4"), color: "from-[hsl(45,90%,50%)] to-[hsl(30,85%,45%)]" },
  ];

  const capabilities = [
    { icon: Bot, title: t("landing.cap1Title"), desc: t("landing.cap1Desc") },
    { icon: TrendingUp, title: t("landing.cap2Title"), desc: t("landing.cap2Desc") },
    { icon: Layers, title: t("landing.cap3Title"), desc: t("landing.cap3Desc") },
    { icon: Shield, title: t("landing.cap4Title"), desc: t("landing.cap4Desc") },
    { icon: Globe, title: t("landing.cap5Title"), desc: t("landing.cap5Desc") },
    { icon: CreditCard, title: t("landing.cap6Title"), desc: t("landing.cap6Desc") },
  ];

  const stagger = {
    hidden: {},
    show: { transition: { staggerChildren: 0.1 } },
  };

  const fadeUp = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" as const } },
  };

  return (
    <main
      className="min-h-screen bg-background overflow-x-hidden select-none"
      style={{ WebkitUserSelect: 'none', MozUserSelect: 'none', msUserSelect: 'none' } as React.CSSProperties}
      onCopy={(e) => e.preventDefault()}
      onCut={(e) => e.preventDefault()}
      onContextMenu={(e) => e.preventDefault()}
      onSelect={(e) => e.preventDefault()}
      onDragStart={(e) => e.preventDefault()}
      onSelectCapture={(e) => e.preventDefault()}
    >
      {/* ===== Stripe-style animated flowing gradient background — full landing page ===== */}
      <div className="pointer-events-none fixed inset-0 z-0 overflow-hidden" aria-hidden="true">
        <div className="stripe-gradient-layer stripe-layer-1" />
        <div className="stripe-gradient-layer stripe-layer-2" />
        <div className="stripe-gradient-layer stripe-layer-3" />
        <div className="stripe-gradient-layer stripe-layer-4" />
        <div className="stripe-gradient-layer stripe-layer-5" />
      </div>

      {/* ===== TOP BAR ===== */}
      <motion.div
        initial={{ y: -60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-4 sm:px-6 py-2.5 glass-strong"
      >
        <div className="flex items-center gap-3">
          <motion.img
            alt="Vazoul"
            className="h-8 w-8 rounded-xl"
            loading="eager"
            width={32}
            height={32}
            src="/lovable-uploads/vazoul-logo-64-small.webp"
            whileHover={{ rotate: 12, scale: 1.1 }}
            transition={{ type: "spring", stiffness: 300 }}
          />
          <span className="text-sm font-bold text-foreground tracking-tight">Vazoul AI</span>
        </div>
        <div className="flex items-center gap-2">
          <LanguageSelector />
          <button
            onClick={toggleTheme}
            className="flex items-center justify-center h-9 w-9 rounded-lg hover:bg-secondary/50 text-muted-foreground hover:text-foreground transition-colors"
            title={theme === "dark" ? t("theme.switchToLight") : t("theme.switchToDark")}
          >
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
        </div>
      </motion.div>

      {/* ===== HERO ===== */}
      <section className="relative min-h-[100dvh] flex items-center pt-14 sm:pt-0">

        <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-16 lg:py-0">
          <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-10 sm:gap-16 lg:gap-24 items-center">
            {/* Left: Copy */}
            <motion.div
              variants={stagger}
              initial="hidden"
              animate="show"
              className="space-y-6 sm:space-y-8"
            >
              {/* Badge */}
              <motion.div variants={fadeUp} className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass text-[11px] sm:text-xs font-semibold text-primary">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
                </span>
                {t("landing.trialBadge")}
              </motion.div>

              {/* Headline */}
              <motion.div variants={fadeUp} className="space-y-4">
                <h1 className="text-4xl sm:text-5xl lg:text-[3.75rem] font-black tracking-tight leading-[1.08]">
                  {t("landing.heroLine1")}{" "}
                  <motion.span
                    className="gradient-text inline-block"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.4, duration: 0.6, type: "spring" }}
                  >
                    {t("landing.heroLine2")}
                  </motion.span>
                  <br className="hidden sm:block" />
                  {t("landing.heroLine3")}
                </h1>
                <motion.p
                  variants={fadeUp}
                  className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-xl"
                >
                  {t("landing.heroSubtitle")}
                </motion.p>
              </motion.div>

              {/* Pillars row */}
              <motion.div variants={fadeUp} className="flex flex-wrap gap-2 sm:gap-3">
                {pillars.map((p, i) => {
                  const Icon = p.icon;
                  return (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.5 + i * 0.1, type: "spring", stiffness: 200 }}
                      whileHover={{ scale: 1.08, y: -2 }}
                      className="flex items-center gap-2 px-3 py-2 rounded-xl glass text-xs font-semibold text-foreground cursor-default"
                    >
                      <div className={`h-6 w-6 rounded-md bg-gradient-to-br ${p.color} flex items-center justify-center`}>
                        <Icon className="h-3.5 w-3.5 text-primary-foreground" />
                      </div>
                      {p.label}
                    </motion.div>
                  );
                })}
              </motion.div>

              {/* Metrics with animated counters */}
              <motion.div variants={fadeUp} className="grid grid-cols-4 gap-2 sm:gap-3 max-w-lg">
                {[
                  { value: "30", suffix: "+", label: t("landing.metricTools") },
                  { value: "10", suffix: "x", label: t("landing.metricSpeed") },
                  { value: "2", suffix: "K+", label: t("landing.metricSellers") },
                  { value: "99", suffix: "%", label: t("landing.metricUptime") },
                ].map((m, i) => (
                  <motion.div
                    key={i}
                    whileHover={{ scale: 1.05, boxShadow: "0 0 20px hsl(262 80% 65% / 0.15)" }}
                    className="text-center py-3 rounded-xl glass transition-shadow"
                  >
                    <AnimatedCounter value={m.value} suffix={m.suffix} />
                    <p className="text-[10px] text-muted-foreground uppercase tracking-[0.15em] mt-1 font-semibold">{m.label}</p>
                  </motion.div>
                ))}
              </motion.div>

              {/* Checklist */}
              <motion.div variants={fadeUp} className="space-y-2 hidden sm:block">
                {[t("landing.check1"), t("landing.check2"), t("landing.check3")].map((text, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.8 + i * 0.15 }}
                    className="flex items-center gap-2.5 text-sm text-muted-foreground"
                  >
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.9 + i * 0.15, type: "spring", stiffness: 400 }}
                      className="h-5 w-5 rounded-full bg-primary/15 flex items-center justify-center"
                    >
                      <Check className="h-3 w-3 text-primary" />
                    </motion.div>
                    {text}
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>

            {/* Right: Auth Card */}
            <motion.div
              initial={{ opacity: 0, x: 40, rotateY: -5 }}
              animate={{ opacity: 1, x: 0, rotateY: 0 }}
              transition={{ duration: 0.8, delay: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="w-full max-w-md mx-auto lg:mx-0 lg:ml-auto"
            >
              <div className="relative">
                <motion.div
                  animate={{ opacity: [0.4, 0.7, 0.4] }}
                  transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                  className="absolute -inset-1 rounded-3xl bg-gradient-to-b from-primary/20 via-transparent to-transparent blur-xl pointer-events-none"
                />
                <div className="relative glass-strong rounded-2xl sm:rounded-3xl p-5 sm:p-8 space-y-5">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={isLogin ? "login" : "signup"}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.25 }}
                      className="text-center space-y-1.5"
                    >
                      <h2 className="text-lg sm:text-xl font-bold text-foreground">
                        {isLogin ? t("landing.welcomeBack") : t("landing.startTrial")}
                      </h2>
                      <p className="text-sm text-muted-foreground">
                        {isLogin ? t("landing.signInSubtitle") : t("landing.signUpSubtitle")}
                      </p>
                    </motion.div>
                  </AnimatePresence>

                  <form onSubmit={handleSubmit} className="space-y-3.5">
                    <AnimatePresence>
                      {!isLogin && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="overflow-hidden"
                        >
                          <div className="space-y-1.5 pb-0.5">
                            <label className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">{t("landing.displayName")}</label>
                            <input type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder={t("landing.displayNamePlaceholder")} required={!isLogin}
                              className="w-full bg-background/60 border border-border rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/20 transition-all" />
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                    <div className="space-y-1.5">
                      <label className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">{t("landing.email")}</label>
                      <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required
                        className="w-full bg-background/60 border border-border rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/20 transition-all" />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">{t("landing.password")}</label>
                      <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required minLength={6} autoComplete={isLogin ? "current-password" : "new-password"}
                        className="w-full bg-background/60 border border-border rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/20 transition-all" />
                    </div>
                    {isLogin && (
                      <div className="flex justify-end -mt-1">
                        <button type="button" onClick={() => setForgotPassword(true)} className="text-xs text-primary hover:underline">
                          {t("landing.forgotPassword")}
                        </button>
                      </div>
                    )}

                    <AnimatePresence>
                      {!isLogin && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden"
                        >
                          <div className="flex items-start gap-3 pb-0.5">
                            <button
                              type="button"
                              role="checkbox"
                              aria-checked={policyAccepted}
                              onClick={() => setPolicyAccepted(!policyAccepted)}
                              className={`mt-0.5 h-4 w-4 min-w-[16px] rounded border-2 flex items-center justify-center transition-all ${
                                policyAccepted
                                  ? "bg-primary border-primary"
                                  : "border-muted-foreground/40 bg-background/60"
                              }`}
                            >
                              {policyAccepted && <Check className="h-3 w-3 text-primary-foreground" />}
                            </button>
                            <span className="text-xs text-muted-foreground leading-relaxed">
                              {t("landing.policyAgree")}{" "}
                              <button type="button" onClick={() => setShowPolicyModal("terms")} className="text-primary hover:underline font-semibold">
                                {t("landing.policyLink")}
                              </button>
                            </span>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    <motion.button
                      type="submit"
                      disabled={loading || (!isLogin && !policyAccepted)}
                      whileHover={{ scale: 1.02, boxShadow: "0 8px 25px hsl(262 80% 65% / 0.3)" }}
                      whileTap={{ scale: 0.97 }}
                      className="w-full btn-gradient text-primary-foreground py-3 rounded-xl text-sm font-bold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                      {isLogin ? t("landing.signIn") : t("landing.startFreeTrial")}
                    </motion.button>

                    <div className="relative my-1">
                      <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border/50" /></div>
                      <div className="relative flex justify-center text-xs">
                        <span className="bg-card/80 px-3 text-muted-foreground/60">{t("landing.orContinueWith")}</span>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <motion.button
                        type="button"
                        whileHover={{ scale: 1.03, y: -1 }}
                        whileTap={{ scale: 0.97 }}
                        onClick={async () => {
                          const { error } = await lovable.auth.signInWithOAuth("google", { redirect_uri: window.location.origin });
                          if (error) toast.error(error.message || "Google sign-in failed");
                        }}
                        className="flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium border border-border bg-background/40 text-foreground hover:bg-muted/60 transition-colors"
                      >
                        <svg className="h-4 w-4" viewBox="0 0 24 24">
                          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                        </svg>
                        Google
                      </motion.button>
                      <motion.button
                        type="button"
                        whileHover={{ scale: 1.03, y: -1 }}
                        whileTap={{ scale: 0.97 }}
                        onClick={async () => {
                          const { error } = await lovable.auth.signInWithOAuth("apple", { redirect_uri: window.location.origin });
                          if (error) toast.error(error.message || "Apple sign-in failed");
                        }}
                        className="flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium border border-border bg-background/40 text-foreground hover:bg-muted/60 transition-colors"
                      >
                        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                        </svg>
                        Apple
                      </motion.button>
                    </div>

                    <p className="text-center text-xs text-muted-foreground">
                      {isLogin ? t("landing.noAccount") : t("landing.haveAccount")}{" "}
                      <button type="button" onClick={() => setIsLogin(!isLogin)} className="text-primary hover:underline font-semibold">
                        {isLogin ? t("landing.signUp") : t("landing.signInLink")}
                      </button>
                    </p>
                  </form>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Scroll cue */}
        <motion.button
          aria-label="Scroll to features"
          onClick={scrollToFeatures}
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 text-muted-foreground/40 hover:text-muted-foreground transition-colors"
        >
          <ChevronDown className="h-6 w-6" />
        </motion.button>
      </section>

      {/* ===== CAPABILITIES GRID ===== */}
      <section id="features" className="relative py-16 sm:py-28">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/[0.015] to-transparent pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6">
          <RevealSection className="text-center space-y-3 mb-10 sm:mb-16">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass text-xs font-semibold text-primary">
              <Zap className="h-3 w-3" /> {t("landing.capTag")}
            </span>
            <h2 className="text-2xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-tight">
              {t("landing.capTitle1")}<br />
              <span className="gradient-text">{t("landing.capTitle2")}</span>
            </h2>
            <p className="text-muted-foreground text-sm sm:text-base max-w-2xl mx-auto">{t("landing.capSubtitle")}</p>
          </RevealSection>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {capabilities.map((cap, i) => {
              const Icon = cap.icon;
              return (
                <RevealSection key={i} delay={i * 0.1}>
                  <motion.div
                    whileHover={{ y: -6, boxShadow: "0 16px 40px hsl(262 80% 65% / 0.1)" }}
                    transition={{ type: "spring", stiffness: 300 }}
                    className="group relative rounded-2xl border border-border bg-card/30 p-5 sm:p-7 space-y-3 hover:border-primary/25 transition-colors h-full"
                  >
                    <motion.div
                      whileHover={{ rotate: 8, scale: 1.1 }}
                      className="h-11 w-11 rounded-xl btn-gradient flex items-center justify-center text-primary-foreground shadow-md"
                    >
                      <Icon className="h-5 w-5" />
                    </motion.div>
                    <h3 className="text-sm sm:text-base font-bold text-foreground">{cap.title}</h3>
                    <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">{cap.desc}</p>
                    <div className="absolute bottom-0 left-5 right-5 h-[2px] bg-gradient-to-r from-transparent via-primary/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  </motion.div>
                </RevealSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section className="relative py-16 sm:py-28 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-[30%] right-[5%] w-[400px] h-[400px] rounded-full bg-[hsl(var(--gradient-pink)/0.04)] blur-[120px]" />
          <div className="absolute bottom-[20%] left-[10%] w-[300px] h-[300px] rounded-full bg-primary/[0.05] blur-[100px]" />
        </div>
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6">
          <RevealSection className="text-center space-y-3 mb-12 sm:mb-16">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass text-xs font-semibold text-primary">
              <Play className="h-3 w-3" /> {t("landing.howItWorksTag")}
            </span>
            <h2 className="text-2xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-tight">
              {t("landing.howItWorksTitle1")}{" "}
              <span className="gradient-text">{t("landing.howItWorksTitle2")}</span>
            </h2>
            <p className="text-muted-foreground text-sm sm:text-base max-w-2xl mx-auto">{t("landing.howItWorksSubtitle")}</p>
          </RevealSection>

          <div className="grid sm:grid-cols-3 gap-6 sm:gap-8">
            {[
              { icon: Search, step: "01", title: t("landing.step1Title"), desc: t("landing.step1Desc"), gradient: "from-[hsl(262,80%,60%)] to-[hsl(280,75%,55%)]" },
              { icon: Sparkles, step: "02", title: t("landing.step2Title"), desc: t("landing.step2Desc"), gradient: "from-[hsl(330,85%,60%)] to-[hsl(350,80%,55%)]" },
              { icon: TrendingUp, step: "03", title: t("landing.step3Title"), desc: t("landing.step3Desc"), gradient: "from-[hsl(145,70%,45%)] to-[hsl(160,65%,40%)]" },
            ].map((s, i) => {
              const Icon = s.icon;
              return (
                <RevealSection key={i} delay={i * 0.15}>
                  <motion.div
                    whileHover={{ y: -8, boxShadow: "0 20px 50px hsl(var(--primary) / 0.12)" }}
                    transition={{ type: "spring", stiffness: 300 }}
                    className="relative group rounded-2xl border border-border/60 bg-card/40 backdrop-blur-sm p-6 sm:p-8 text-center space-y-4 hover:border-primary/30 transition-all duration-500"
                  >
                    {/* Step number */}
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className={`inline-flex items-center justify-center h-7 w-7 rounded-full bg-gradient-to-br ${s.gradient} text-[11px] font-black text-primary-foreground shadow-lg`}>
                        {s.step}
                      </span>
                    </div>
                    {/* Connecting line (hidden on first/last or mobile) */}
                    {i < 2 && (
                      <div className="hidden sm:block absolute top-3.5 -right-4 sm:-right-5 w-8 sm:w-10 h-px bg-gradient-to-r from-border via-primary/20 to-border" />
                    )}
                    <motion.div
                      whileHover={{ rotate: 8, scale: 1.1 }}
                      className={`mx-auto h-14 w-14 rounded-2xl bg-gradient-to-br ${s.gradient} flex items-center justify-center shadow-lg mt-2`}
                    >
                      <Icon className="h-6 w-6 text-primary-foreground" />
                    </motion.div>
                    <h3 className="text-base sm:text-lg font-bold text-foreground">{s.title}</h3>
                    <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
                    <div className="absolute bottom-0 left-6 right-6 h-[2px] bg-gradient-to-r from-transparent via-primary/25 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  </motion.div>
                </RevealSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== SOCIAL PROOF ===== */}
      <section className="py-12 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <RevealSection>
            <div className="glass rounded-2xl sm:rounded-3xl p-6 sm:p-10">
              <div className="grid sm:grid-cols-3 gap-6 sm:gap-10">
                {[
                  { icon: Shield, title: t("landing.proofTitle1"), desc: t("landing.proofDesc1") },
                  { icon: Zap, title: t("landing.proofTitle2"), desc: t("landing.proofDesc2") },
                  { icon: Globe, title: t("landing.proofTitle3"), desc: t("landing.proofDesc3") },
                ].map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.15, duration: 0.5 }}
                      className="flex gap-4"
                    >
                      <motion.div
                        whileHover={{ scale: 1.15, rotate: 5 }}
                        className="h-11 w-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0"
                      >
                        <Icon className="h-5 w-5" />
                      </motion.div>
                      <div>
                        <h3 className="text-sm font-bold text-foreground mb-1">{item.title}</h3>
                        <p className="text-xs text-muted-foreground leading-relaxed">{item.desc}</p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </RevealSection>
        </div>
      </section>

      {/* ===== COMMUNITY / TESTIMONIALS ===== */}
      <section className="py-20 sm:py-32 relative overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
          <div className="absolute top-[20%] left-[15%] w-[350px] h-[350px] rounded-full bg-[hsl(330,85%,60%,0.03)] blur-[100px]" />
          <div className="absolute bottom-[20%] right-[15%] w-[300px] h-[300px] rounded-full bg-primary/[0.04] blur-[100px]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
          {/* Section header */}
          <RevealSection className="text-center space-y-5 mb-14 sm:mb-20">
            <motion.div
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ type: "spring", stiffness: 200, delay: 0.1 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass text-xs font-semibold text-primary"
            >
              <Users className="h-3.5 w-3.5" />
              {t("landing.communityTag") || "Community"}
            </motion.div>

            <h2 className="text-3xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.1]">
              <span className="text-foreground">{t("landing.communitySlogan1") || "People love"}</span>
              <br />
              <motion.span
                className="gradient-text inline-block"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3, duration: 0.6 }}
              >
                {t("landing.communitySlogan2")}
              </motion.span>
            </h2>

            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="text-muted-foreground text-sm sm:text-lg max-w-xl mx-auto"
            >
              {t("landing.communityDesc") || "Discover why E-commerces choose Vazoul every day."}
            </motion.p>

            {/* Stats row */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
              className="flex items-center justify-center gap-6 sm:gap-10 pt-2"
            >
              {[
                { value: "4.9", label: t("landing.avgRating") || "Avg Rating" },
                { value: "2K+", label: t("landing.happyUsers") || "Happy Users" },
                { value: "15+", label: t("landing.countries") || "Countries" },
              ].map((stat, i) => (
                <div key={i} className="text-center">
                  <span className="text-lg sm:text-2xl font-black gradient-text">{stat.value}</span>
                  <p className="text-[9px] sm:text-[10px] text-muted-foreground uppercase tracking-[0.15em] font-semibold mt-0.5">{stat.label}</p>
                </div>
              ))}
            </motion.div>
          </RevealSection>

          {/* Scrolling reviews - Row 1 */}
          <div className="relative mb-5 sm:mb-6 overflow-hidden" dir="ltr">
            <div className="absolute left-0 top-0 bottom-0 w-20 sm:w-32 bg-gradient-to-r from-background to-transparent z-10 pointer-events-none" />
            <div className="absolute right-0 top-0 bottom-0 w-20 sm:w-32 bg-gradient-to-l from-background to-transparent z-10 pointer-events-none" />
            <div className="flex gap-5 animate-scroll-left">
              {[...TESTIMONIALS_ROW1, ...TESTIMONIALS_ROW1].map((review, i) => (
                <TestimonialCard key={i} review={review} index={i} />
              ))}
            </div>
          </div>

          {/* Scrolling reviews - Row 2 (hidden on mobile for performance) */}
          <div className="relative overflow-hidden hidden sm:block" dir="ltr">
            <div className="absolute left-0 top-0 bottom-0 w-20 sm:w-32 bg-gradient-to-r from-background to-transparent z-10 pointer-events-none" />
            <div className="absolute right-0 top-0 bottom-0 w-20 sm:w-32 bg-gradient-to-l from-background to-transparent z-10 pointer-events-none" />
            <div className="flex gap-5 animate-scroll-right">
              {[...TESTIMONIALS_ROW2, ...TESTIMONIALS_ROW2].map((review, i) => (
                <TestimonialCard key={i} review={review} index={i} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== ABOUT US ===== */}
      <section className="relative py-16 sm:py-24 border-t border-border/30">
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-[20%] left-[10%] w-[300px] h-[300px] rounded-full bg-primary/[0.04] blur-[100px]" />
          <div className="absolute bottom-[10%] right-[15%] w-[250px] h-[250px] rounded-full bg-[hsl(330,85%,60%,0.04)] blur-[80px]" />
        </div>
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6">
          <RevealSection className="text-center space-y-4 mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass text-xs font-semibold text-primary">
              <Award className="h-3.5 w-3.5" />
              {t("landing.aboutTag")}
            </div>
            <h2 className="text-3xl sm:text-5xl font-black tracking-tight leading-[1.1]">
              <span className="text-foreground">{t("landing.aboutTitle1")}</span>{" "}
              <span className="gradient-text">{t("landing.aboutTitle2")}</span>
            </h2>
            <p className="text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              {t("landing.aboutDesc")}
            </p>
          </RevealSection>

          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { icon: Rocket, title: t("landing.aboutVal1Title"), desc: t("landing.aboutVal1Desc"), gradient: "from-[hsl(262,80%,60%)] to-[hsl(280,75%,55%)]" },
              { icon: Handshake, title: t("landing.aboutVal2Title"), desc: t("landing.aboutVal2Desc"), gradient: "from-[hsl(330,85%,60%)] to-[hsl(350,80%,55%)]" },
              { icon: Globe, title: t("landing.aboutVal3Title"), desc: t("landing.aboutVal3Desc"), gradient: "from-[hsl(145,70%,45%)] to-[hsl(160,65%,40%)]" },
            ].map((v, i) => {
              const Icon = v.icon;
              return (
                <RevealSection key={i} delay={i * 0.1}>
                  <div className="group relative rounded-2xl border border-border/60 bg-card/50 backdrop-blur-sm p-6 text-center space-y-4 hover:border-primary/30 transition-all duration-500 hover:shadow-[0_8px_30px_-8px_hsl(var(--primary)/0.12)]">
                    <div className={`mx-auto h-12 w-12 rounded-xl bg-gradient-to-br ${v.gradient} flex items-center justify-center shadow-lg`}>
                      <Icon className="h-6 w-6 text-primary-foreground" />
                    </div>
                    <h3 className="text-base font-bold text-foreground">{v.title}</h3>
                    <p className="text-xs text-muted-foreground leading-relaxed">{v.desc}</p>
                  </div>
                </RevealSection>
              );
            })}
          </div>
        </div>
      </section>


      {/* ===== CTA ===== */}
      <section className="relative py-16 sm:py-28">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
          <motion.div
            animate={{ scale: [1, 1.2, 1], opacity: [0.04, 0.08, 0.04] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[300px] rounded-full bg-primary blur-[100px]"
          />
        </div>
        <RevealSection className="relative max-w-3xl mx-auto px-4 sm:px-6 text-center space-y-6">
          <h2 className="text-2xl sm:text-4xl font-black text-foreground leading-tight">
            {t("landing.ctaLine1")}{" "}
            <span className="gradient-text">{t("landing.ctaLine2")}</span>
          </h2>
          <p className="text-sm sm:text-base text-muted-foreground max-w-lg mx-auto">{t("landing.ctaDesc")}</p>
          <motion.button
            whileHover={{ scale: 1.05, boxShadow: "0 12px 35px hsl(262 80% 65% / 0.35)" }}
            whileTap={{ scale: 0.97 }}
            onClick={() => { setIsLogin(false); window.scrollTo({ top: 0, behavior: "smooth" }); }}
            className="inline-flex items-center gap-2 px-10 py-3.5 rounded-xl text-sm font-bold btn-gradient text-primary-foreground transition-colors"
          >
            {t("landing.ctaButton")}
            <motion.span animate={{ x: [0, 4, 0] }} transition={{ duration: 1.5, repeat: Infinity }}>
              <ArrowRight className="h-4 w-4" />
            </motion.span>
          </motion.button>
        </RevealSection>
      </section>

      {/* ===== MARQUEE ===== */}
      <RevealSection>
        <section className="py-12 border-t border-border/30 overflow-hidden">
          <div className="text-center mb-8">
            <p className="text-sm text-muted-foreground">
              {t("landing.marqueeText")}
            </p>
          </div>
          <div className="relative overflow-hidden" dir="ltr">
            <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-background to-transparent z-10 pointer-events-none" />
            <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-background to-transparent z-10 pointer-events-none" />
            <div className="ticker-track">
              {[0, 1].map((setIdx) => (
                <div key={setIdx} className="ticker-group" aria-hidden={setIdx > 0}>
                  {MARQUEE_BRANDS.map((brand, i) => (
                    <span key={`${setIdx}-${i}`} className="flex items-center gap-3 whitespace-nowrap select-none min-w-max">
                      {brand.logo ? (
                        <img
                          src={brand.logo}
                          alt={brand.name}
                          className="h-10 w-10 object-contain"
                          loading="lazy"
                          decoding="async"
                          width={40}
                          height={40}
                        />
                      ) : (
                        <span
                          className="h-10 w-10 rounded-lg flex items-center justify-center text-sm font-bold text-white"
                          style={{ backgroundColor: brand.color }}
                        >
                          {brand.name.charAt(0)}
                        </span>
                      )}
                      <span className="text-xl font-semibold tracking-tight text-foreground/70">
                        {brand.name}
                      </span>
                    </span>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </section>
      </RevealSection>



      {/* ===== POLICY MODAL ===== */}
      <AnimatePresence>
        {showPolicyModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-background/80 backdrop-blur-sm p-4"
            onClick={() => { setPolicyRead(true); setShowPolicyModal(null); }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 25 }}
              className="bg-card border border-border rounded-2xl max-w-lg w-full max-h-[80vh] overflow-hidden shadow-xl flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-border/50 flex items-center justify-between">
                <div className="flex gap-2">
                  <button type="button" onClick={() => setShowPolicyModal("terms")}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors ${showPolicyModal === "terms" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground"}`}>
                    {t("landing.termsOfService")}
                  </button>
                  <button type="button" onClick={() => setShowPolicyModal("privacy")}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors ${showPolicyModal === "privacy" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground"}`}>
                    {t("landing.privacyPolicy")}
                  </button>
                </div>
              </div>
              <PolicyModalContent type={showPolicyModal} />
              <div className="px-6 py-3 border-t border-border/50">
                <button type="button" onClick={() => { setPolicyRead(true); setShowPolicyModal(null); }} className="w-full btn-gradient text-primary-foreground py-2.5 rounded-xl text-sm font-bold">
                  {t("common.close")}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ===== FORGOT PASSWORD MODAL ===== */}
      <AnimatePresence>
        {forgotPassword && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-background/80 backdrop-blur-md flex items-center justify-center p-6"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              transition={{ type: "spring", stiffness: 300, damping: 25 }}
              className="w-full max-w-md glass-strong rounded-3xl p-8 space-y-4"
            >
              <div className="text-center space-y-2">
                <h2 className="text-lg font-bold text-foreground">{t("landing.resetTitle")}</h2>
                <p className="text-sm text-muted-foreground">{t("landing.resetSubtitle")}</p>
              </div>
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div className="space-y-2">
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t("landing.email")}</label>
                  <input type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} placeholder="you@example.com" required
                    className="w-full bg-background/60 border border-border rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/20 transition-all" />
                </div>
                <div className="flex gap-3">
                  <button type="button" onClick={() => { setForgotPassword(false); setForgotEmail(""); }}
                    className="flex-1 py-2.5 rounded-xl text-sm font-medium border border-border text-muted-foreground hover:text-foreground transition-colors">
                    {t("common.cancel")}
                  </button>
                  <motion.button
                    type="submit"
                    disabled={forgotLoading}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.97 }}
                    className="flex-1 btn-gradient text-primary-foreground py-2.5 rounded-xl text-sm font-bold disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {forgotLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                    {t("landing.sendResetLink")}
                  </motion.button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
